import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Select, DatePicker, Tag, Input, InputNumber, Button, Checkbox, Pagination, Modal, message } from 'antd';
import { ReloadOutlined, UndoOutlined, DeleteOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import config from '../../config';
import { NdoePage, NdoePageHeader, NdoeLoading, NdoeError, NdoeResponsiveTable, NdoeDataCard } from '../../components/ndoe';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
const { RangePicker } = DatePicker;

// Couleur de tag par sévérité de source (renvoyée par le backend : danger/warn/info).
const TONE_COLOR = { danger: 'volcano', warn: 'gold', info: 'blue', neutral: 'default' };

// Journal des SUPPRESSIONS (admin) : feed transversal de tout ce qui a été supprimé /
// annulé dans l'ERP (commandes, ventes, clientes, produits, échanges, paiements,
// acomptes, réservations, précommandes + suppressions hors-app). Lecture seule +
// restauration guardée (admin) des soft-deletes réversibles, avec recap de confirmation.
const JournalSuppressions = () => {
  const role = useSelector((s) => s.user?.currentUser?.role);
  const isAdmin = role === 'admin';

  const [feed, setFeed] = useState(null);
  const [filtres, setFiltres] = useState({ sources: [], auteurs: [], total: 0 });
  const [page, setPage] = useState(1);
  const [range, setRange] = useState(null);
  const [sources, setSources] = useState([]);
  const [auteur, setAuteur] = useState(undefined);
  const [client, setClient] = useState('');
  const [q, setQ] = useState('');
  const [montantMin, setMontantMin] = useState(null);
  const [montantMax, setMontantMax] = useState(null);
  const [sansTrace, setSansTrace] = useState(false);
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [restoreCible, setRestoreCible] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [resetKey, setResetKey] = useState(0); // force le remount du champ de recherche (non contrôlé) au reset
  const reqSeq = useRef(0); // garde anti-réponse périmée (les filtres réactifs peuvent revenir dans le désordre)

  useEffect(() => {
    axios.get(`${DOMAIN}/api/audit/suppressions/filtres`)
      .then(({ data }) => setFiltres(data || { sources: [], auteurs: [], total: 0 }))
      .catch(() => { /* filtres optionnels */ });
  }, []);

  const charger = async () => {
    const seq = ++reqSeq.current;
    setLoading(true); setErreur(null);
    try {
      const params = { page: String(page), pageSize: '25' };
      if (q.trim()) params.q = q.trim();
      if (client.trim()) params.client = client.trim();
      if (sources.length) params.sources = sources.join(',');
      if (auteur != null) params.id_user = auteur;
      if (montantMin != null && montantMin !== '') params.montant_min = montantMin;
      if (montantMax != null && montantMax !== '') params.montant_max = montantMax;
      if (sansTrace) params.sans_trace = '1';
      if (range?.[0]) params.date_debut = range[0].format('YYYY-MM-DD');
      if (range?.[1]) params.date_fin = range[1].add(1, 'day').format('YYYY-MM-DD');
      const { data } = await axios.get(`${DOMAIN}/api/audit/suppressions`, { params });
      if (seq === reqSeq.current) setFeed(data); // ignore les réponses périmées (frappe rapide)
    } catch (e) {
      if (seq === reqSeq.current) setErreur(e.response?.data?.error || e.message);
    } finally { if (seq === reqSeq.current) setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { charger(); }, [page, range, sources, auteur, client, q, montantMin, montantMax, sansTrace]);
  const resetPage = (fn) => (v) => { setPage(1); fn(v); };

  const nbFiltres = (range ? 1 : 0) + (sources.length ? 1 : 0) + (auteur != null ? 1 : 0)
    + (client.trim() ? 1 : 0) + (q.trim() ? 1 : 0) + (montantMin != null ? 1 : 0)
    + (montantMax != null ? 1 : 0) + (sansTrace ? 1 : 0);
  const reinit = () => { setRange(null); setSources([]); setAuteur(undefined); setClient(''); setQ(''); setMontantMin(null); setMontantMax(null); setSansTrace(false); setPage(1); setResetKey((k) => k + 1); };

  const money = (n) => (n == null ? '—' : `${Number(n).toLocaleString('fr-FR')} $`);
  const srcTag = (l) => <Tag color={TONE_COLOR[l.tone] || 'default'}>{l.source_label}</Tag>;
  const dateFmt = (d) => (d ? format(new Date(d), 'dd/MM/yyyy HH:mm') : '—');

  const confirmerRestore = async () => {
    if (!restoreCible) return;
    setRestoring(true);
    try {
      const { data } = await axios.put(
        `${DOMAIN}/api/audit/suppressions/${restoreCible.source}/${restoreCible.ref_id}/restaurer`,
      );
      message.success(data?.message || 'Restauré.');
      if (data?.result?.avertissement) message.warning(data.result.avertissement, 6);
      setRestoreCible(null);
      charger();
    } catch (e) {
      message.error(e.response?.data?.error || 'Restauration impossible.');
    } finally { setRestoring(false); }
  };

  const columns = useMemo(() => [
    { title: 'Date suppr.', dataIndex: 'date_suppression', width: 140, render: dateFmt },
    { title: 'Type', key: 'type', width: 150, render: (_, l) => srcTag(l) },
    { title: 'Élément', dataIndex: 'libelle' },
    { title: 'Cliente', dataIndex: 'client', render: (c) => c || '—' },
    { title: 'Montant', dataIndex: 'montant', align: 'right', render: money },
    { title: 'Auteur', dataIndex: 'auteur_affiche', render: (a, l) => (l.audite ? a : <Tag color="warning">{a}</Tag>) },
    { title: 'Motif', dataIndex: 'motif', render: (m) => m || '—' },
    {
      title: '', key: 'act', width: 120, render: (_, l) => (l.restaurable
        ? <Button size="small" icon={<UndoOutlined />} onClick={() => setRestoreCible(l)}>Restaurer</Button>
        : <span style={{ color: 'var(--ndoe-muted)', fontSize: 12 }}>—</span>),
    },
  ], []);

  const renderCard = (l) => (
    <NdoeDataCard
      title={l.libelle}
      subtitle={dateFmt(l.date_suppression)}
      badge={srcTag(l)}
      metrics={[
        { label: 'Cliente', value: l.client || '—' },
        { label: 'Montant', value: money(l.montant) },
        { label: 'Auteur', value: l.auteur_affiche },
      ]}
      actions={l.restaurable ? <Button size="small" icon={<UndoOutlined />} onClick={() => setRestoreCible(l)}>Restaurer</Button> : null}
    >
      {l.motif ? <div style={{ fontSize: 13, color: 'var(--ndoe-muted)' }}>{l.motif}</div> : null}
    </NdoeDataCard>
  );

  if (!isAdmin) return <NdoePage><NdoeError text="Accès réservé à l’administrateur." /></NdoePage>;

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Administration"
        title="Journal des suppressions"
        subtitle="Tout ce qui a été supprimé ou annulé dans l’application — commandes, ventes, clientes, produits, échanges, paiements, acomptes, réservations + suppressions faites directement en base. Qui, quand, pourquoi — filtrable, avec restauration des cas réversibles."
      />

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', margin: '10px 0 16px' }}>
        <RangePicker value={range} onChange={resetPage(setRange)} format="DD/MM/YYYY" allowEmpty={[true, true]} placeholder={['Suppr. du', 'au']} />
        <Select mode="multiple" allowClear showSearch optionFilterProp="label" placeholder="Type(s)" style={{ minWidth: 220 }}
          value={sources} onChange={resetPage(setSources)} maxTagCount="responsive"
          options={filtres.sources.map((s) => ({ value: s.value, label: `${s.label} (${s.n})` }))} />
        <Select allowClear showSearch optionFilterProp="label" placeholder="Auteur" style={{ minWidth: 150 }}
          value={auteur} onChange={resetPage(setAuteur)}
          options={filtres.auteurs.map((a) => ({ value: a.value, label: a.label }))} />
        <Input allowClear placeholder="Cliente" style={{ maxWidth: 150 }} value={client}
          onChange={(e) => resetPage(setClient)(e.target.value)} />
        <InputNumber placeholder="Montant min" style={{ width: 120 }} min={0} value={montantMin} onChange={resetPage(setMontantMin)} />
        <InputNumber placeholder="Montant max" style={{ width: 120 }} min={0} value={montantMax} onChange={resetPage(setMontantMax)} />
        <Input.Search key={`q-${resetKey}`} placeholder="Recherche libre (réf, motif…)" allowClear style={{ maxWidth: 230 }} onSearch={resetPage(setQ)} />
        <Checkbox checked={sansTrace} onChange={(e) => resetPage(setSansTrace)(e.target.checked)}>Sans trace d’auteur</Checkbox>
        <Button icon={<ReloadOutlined />} onClick={charger}>Actualiser</Button>
        {nbFiltres > 0 && <Button type="link" onClick={reinit}>Réinitialiser ({nbFiltres})</Button>}
      </div>

      {loading ? <NdoeLoading rows={8} />
        : erreur ? <NdoeError text={erreur} onRetry={charger} />
          : (
            <NdoeResponsiveTable
              rowKey={(r) => `${r.source}-${r.ref_id}`}
              columns={columns}
              data={feed?.lignes || []}
              renderCard={renderCard}
              cardTitreCol="libelle"
              pagination={{ current: page, pageSize: feed?.pageSize || 25, total: feed?.total || 0, showSizeChanger: false, onChange: setPage,
                showTotal: (t) => `${t} suppression(s)` }}
              mobilePager={(feed?.total || 0) > (feed?.pageSize || 25) ? (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
                  <Pagination simple current={page} pageSize={feed?.pageSize || 25} total={feed?.total || 0} onChange={setPage} />
                </div>
              ) : null}
              scroll={{ x: 1100 }}
              empty={{ icon: '🗑️', title: 'Aucune suppression', text: 'Rien à afficher pour ces filtres.' }}
            />
          )}

      <Modal
        open={!!restoreCible}
        title="Restaurer cet élément ?"
        okText={restoring ? 'Restauration…' : 'Confirmer la restauration'}
        okButtonProps={{ loading: restoring, icon: <UndoOutlined /> }}
        cancelText="Annuler"
        onOk={confirmerRestore}
        onCancel={() => (restoring ? null : setRestoreCible(null))}
      >
        {restoreCible && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <DeleteOutlined style={{ color: 'var(--ndoe-danger, #b74b49)' }} />
              <b>{restoreCible.libelle}</b>
            </div>
            <RecapLigne label="Type" value={restoreCible.source_label} />
            <RecapLigne label="Cliente" value={restoreCible.client || '—'} />
            <RecapLigne label="Montant" value={money(restoreCible.montant)} />
            <RecapLigne label="Supprimé le" value={dateFmt(restoreCible.date_suppression)} />
            <RecapLigne label="Par" value={restoreCible.auteur_affiche} />
            {restoreCible.motif ? <RecapLigne label="Motif" value={restoreCible.motif} /> : null}
            <div style={{ marginTop: 6, fontSize: 13, color: 'var(--ndoe-muted)' }}>
              L’élément redeviendra visible et actif dans l’application. L’opération est tracée à ton nom.
            </div>
          </div>
        )}
      </Modal>
    </NdoePage>
  );
};

const RecapLigne = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13 }}>
    <span style={{ color: 'var(--ndoe-muted)' }}>{label}</span>
    <span style={{ fontWeight: 600, textAlign: 'right' }}>{value}</span>
  </div>
);

export default JournalSuppressions;
